import { useEffect, useState } from 'react';
import { CpDataApi } from 'cp-xhr';
import { DocumentsQuery, PaginatedDocumentsResponse } from './types';
import { serializeQueryParams } from './serializeQueryParams';

export type ServerPaginatedDocumentsState<T> = {
    data?: PaginatedDocumentsResponse<T>;
    isLoading: boolean;
    loadingError?: unknown;
};

type LoadedState<T> = {
    queryKey?: string;
    data?: PaginatedDocumentsResponse<T>;
    loadingError?: unknown;
};

const toParams = (query: DocumentsQuery): Record<string, string | number | undefined> => ({
    page: query.page,
    pageSize: query.pageSize,
    documentType: query.documentType || undefined,
    contractNumber: query.contractNumber || undefined,
    fromDate: query.fromDate || undefined,
    toDate: query.toDate || undefined,
});

export function useServerPaginatedDocuments<T>(
    endpoint: string | null,
    query: DocumentsQuery,
): ServerPaginatedDocumentsState<T> {
    const [loaded, setLoaded] = useState<LoadedState<T>>({});
    const queryKey = endpoint ? `${endpoint}?${serializeQueryParams(toParams(query))}` : undefined;
    const isUpToDate = !queryKey || loaded.queryKey === queryKey;

    useEffect(() => {
        if (!endpoint) {
            setLoaded(previous => (previous.loadingError ? {} : previous));
            return;
        }
        if (isUpToDate) {
            return;
        }

        let cancelled = false;
        const abortController = new AbortController();

        CpDataApi.get<PaginatedDocumentsResponse<T>>(endpoint, {
            signal: abortController.signal,
            params: toParams(query),
            paramsSerializer: serializeQueryParams,
        })
            .then(response => {
                if (!cancelled) {
                    setLoaded({ queryKey, data: response.data });
                }
            })
            .catch(error => {
                if (!cancelled) {
                    setLoaded({ queryKey, loadingError: error });
                }
            });

        return () => {
            cancelled = true;
            abortController.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryKey]);

    if (!queryKey) {
        return { isLoading: false };
    }

    return {
        data: loaded.data,
        isLoading: !isUpToDate,
        loadingError: isUpToDate ? loaded.loadingError : undefined,
    };
}
